/* eslint-disable global-require */
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { FullWidthBannersComponent } from '@notino/react-styleguide'

// types
import { CmsBannersData, SalonPageBannersSlide } from '../../../../types/types'

// styles
import * as SC from './SalonsPageBannerStyles'

// utils
import { PRODUCTION_ENVIRONMENT, SALONS_PAGE_BANNER_SETTINGS } from '../../../../utils/enums'
import { AppContext } from '../../../../utils/appProvider'
import { isExternalLink } from '../../../../utils/helper'
import Modal from '../../../../atoms/Modal/Modal'
import useMessages from '../../../../hooks/useMessages'

type SalonsPageBannerProps = {
	cmsData: CmsBannersData
}

type InfoBannerData = {
	text: string
}

type GetSalonsPageBannersArgs = {
	bannersData: CmsBannersData
	productionEnvironment: PRODUCTION_ENVIRONMENT
	locale: string
	onInfoBannerClick: (data: InfoBannerData) => void
}

export const getSalonsPageBannerSlides = (args: GetSalonsPageBannersArgs): SalonPageBannersSlide[] => {
	const { bannersData, onInfoBannerClick, productionEnvironment, locale } = args

	return bannersData.banners.map((banner) => {
		const { image, infoText, backgroundColor, backgroundImage } = banner
		const editedBackgroundColor = backgroundColor?.replace('#', '')

		let resolvedBackgroundColor

		if (backgroundImage) {
			resolvedBackgroundColor = SALONS_PAGE_BANNER_SETTINGS.DEFAULT_BACKGROUND_COLOR
		} else {
			resolvedBackgroundColor = editedBackgroundColor || SALONS_PAGE_BANNER_SETTINGS.DEFAULT_BACKGROUND_COLOR
		}

		return {
			background: {
				color: resolvedBackgroundColor,
				image: backgroundImage,
				repeat: 0
			},
			image: {
				desktop: image.desktop,
				mobile: image.mobile
			},
			imageAlt: image.alt,
			link: image.link || '',
			isOpenNewTab: image.link ? isExternalLink(image.link, productionEnvironment, locale) : false,
			onInfoButtonClick: infoText ? () => onInfoBannerClick({ text: infoText }) : undefined,
			title: ''
		}
	})
}

// component
const SalonsPageBanner = (props: SalonsPageBannerProps) => {
	const { cmsData } = props
	const { locale, productionEnvironment } = useContext(AppContext)
	const { messages } = useMessages()

	const [infoBannerData, setInfoBannerData] = useState<InfoBannerData | null>(null)

	const slides = useMemo(
		() => getSalonsPageBannerSlides({ bannersData: cmsData, locale, productionEnvironment, onInfoBannerClick: setInfoBannerData }),
		[cmsData, locale, productionEnvironment]
	)

	const [currentSlides, setCurrentSlides] = useState(slides.slice(0, 1))

	useEffect(() => {
		/**
		 * Delay setting all slides until the document is fully loaded for a better CWV results
		 * When there is only one slide, <FullWidthBannersComponent> will not render the slider, but only the simple image with background
		 */
		if (slides.length <= 1) {
			return undefined
		}

		if (document.readyState === 'complete') {
			setCurrentSlides(slides)
			return undefined
		}

		const handleReadyStateChange = () => {
			if (document.readyState === 'complete') {
				setCurrentSlides(slides)
			}
		}

		document.addEventListener('readystatechange', handleReadyStateChange)

		return () => {
			document.removeEventListener('readystatechange', handleReadyStateChange)
		}
	}, [slides])

	return (
		<>
			<SC.SalonsPageBannerContainer>
				<SC.SliderWrapper>
					<FullWidthBannersComponent
						defaultBackground={{
							color: currentSlides[0].background?.color,
							image: currentSlides[0].background?.image,
							repeat: 0
						}}
						items={currentSlides}
						className={'salons-page-banner'}
					/>
				</SC.SliderWrapper>
			</SC.SalonsPageBannerContainer>
			{infoBannerData && (
				<Modal show header={messages.Information} ariaLabel={messages.Information} onClose={() => setInfoBannerData(null)}>
					<SC.ModalText>{infoBannerData.text}</SC.ModalText>
				</Modal>
			)}
		</>
	)
}

export default SalonsPageBanner
